<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    append-to="body"
    :closable="false"
    :draggable="false"
    class="milestone-form-dialog w-full"
    :pt="dialogPt"
    data-testid="milestone-form-dialog"
    @show="handleShow"
    @hide="handleHide"
  >
    <template #header>
      <div class="milestone-form-dialog__header">
        <div class="milestone-form-dialog__header-text">
          <h2 id="milestone-form-dialog-title" class="milestone-form-dialog__title">
            {{ dialogTitle }}
          </h2>
        </div>
        <button
          type="button"
          class="milestone-form-dialog__close"
          :aria-label="$t('ProjectManagement.cancel')"
          data-testid="milestone-form-close"
          @click="close"
        >
          <i class="pi pi-times" aria-hidden="true" />
        </button>
      </div>
    </template>

    <div class="milestone-form-dialog__body">
      <div class="milestone-form-dialog__field">
        <label class="milestone-form-dialog__label" for="milestone-form-title">
          {{ $t('UserProjects.milestoneDialog.nameLabel') }}
          <span class="milestone-form-dialog__required">*</span>
        </label>
        <InputText
          id="milestone-form-title"
          v-model="form.title"
          class="milestone-form-dialog__input w-full"
          :placeholder="$t('UserProjects.milestoneDialog.namePlaceholder')"
          :invalid="!!formErrors.title"
          data-testid="milestone-form-title"
        />
        <small v-if="formErrors.title" class="milestone-form-dialog__error">
          {{ formErrors.title }}
        </small>
      </div>

      <div class="milestone-form-dialog__field">
        <label class="milestone-form-dialog__label" for="milestone-form-due-date">
          {{ $t('UserProjects.milestoneDialog.targetDateLabel') }}
          <span class="milestone-form-dialog__required">*</span>
        </label>
        <InputText
          id="milestone-form-due-date"
          v-model="form.dueDate"
          type="date"
          class="milestone-form-dialog__input w-full"
          :invalid="!!formErrors.dueDate"
          data-testid="milestone-form-due-date"
        />
        <small v-if="formErrors.dueDate" class="milestone-form-dialog__error">
          {{ formErrors.dueDate }}
        </small>
      </div>

      <div class="milestone-form-dialog__field">
        <label class="milestone-form-dialog__label" for="milestone-form-actual-date">
          {{ $t('UserProjects.milestoneDialog.actualDateLabel') }}
        </label>
        <InputText
          id="milestone-form-actual-date"
          v-model="form.actualDate"
          type="date"
          class="milestone-form-dialog__input w-full"
          data-testid="milestone-form-actual-date"
        />
      </div>

      <div class="milestone-form-dialog__field">
        <label class="milestone-form-dialog__label" for="milestone-form-responsible">
          {{ $t('UserProjects.milestoneDialog.responsibleLabel') }}
        </label>
        <UserSelectInput
          v-model="form.responsibleUserId"
          :placeholder="$t('UserProjects.milestoneDialog.responsiblePlaceholder')"
        />
      </div>

      <div class="milestone-form-dialog__field">
        <label class="milestone-form-dialog__label" for="milestone-form-constraints">
          {{ $t('UserProjects.milestoneDialog.constraintsLabel') }}
        </label>
        <MultiSelect
          input-id="milestone-form-constraints"
          v-model="form.constraintIds"
          :options="constraintOptions"
          option-label="label"
          option-value="value"
          display="chip"
          :placeholder="$t('UserProjects.milestoneDialog.constraintsPlaceholder')"
          class="milestone-form-dialog__multiselect w-full"
          :loading="constraintsLoading"
          :empty-message="$t('UserProjects.milestoneDialog.constraintsEmpty')"
          data-testid="milestone-form-constraints"
        />
      </div>

      <div class="milestone-form-dialog__info">
        <i class="pi pi-info-circle milestone-form-dialog__info-icon" aria-hidden="true" />
        <p class="milestone-form-dialog__info-text">
          {{ $t('UserProjects.milestoneDialog.statusAutoHint') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="milestone-form-dialog__footer">
        <button
          type="button"
          class="milestone-form-dialog__btn-cancel"
          data-testid="milestone-form-cancel"
          @click="close"
        >
          {{ $t('ProjectManagement.cancel') }}
        </button>
        <button
          type="button"
          class="milestone-form-dialog__btn-submit"
          :class="{ 'milestone-form-dialog__btn-submit--active': canSubmit }"
          :disabled="!canSubmit || saving"
          data-testid="milestone-form-submit"
          @click="submit"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>{{ submitLabel }}</span>
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import UserSelectInput from '@/components/global/UserSelectInput.vue';
import {
  emptyMilestoneDialogForm,
  mapMilestoneApiToDialog,
  mapMilestoneDialogToApiPayload,
  validateMilestoneDialogForm,
} from '@/utils/milestoneFormMapping';
import { displayConstraintTitle } from '@/utils/constraintFormMapping';

export default {
  name: 'MilestoneFormDialog',
  components: {
    Dialog,
    InputText,
    MultiSelect,
    UserSelectInput,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value),
    },
    initialMilestone: {
      type: Object,
      default: null,
    },
    openConstraints: {
      type: Array,
      default: () => [],
    },
    constraintsLoading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible', 'submit'],
  data() {
    return {
      form: emptyMilestoneDialogForm(),
      formErrors: {},
    };
  },
  computed: {
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    dialogTitle() {
      return this.mode === 'create'
        ? this.$t('UserProjects.milestoneDialog.createTitle')
        : this.$t('UserProjects.milestoneDialog.editTitle');
    },
    submitLabel() {
      return this.mode === 'create'
        ? this.$t('UserProjects.milestoneDialog.createSubmit')
        : this.$t('UserProjects.milestoneDialog.saveSubmit');
    },
    canSubmit() {
      return Boolean(String(this.form.title || '').trim() && this.form.dueDate);
    },
    constraintOptions() {
      return (this.openConstraints || []).map((item) => ({
        label: displayConstraintTitle(item),
        value: item.id,
      }));
    },
    dialogPt() {
      return {
        root: {
          'data-testid': 'milestone-form-dialog',
          'aria-labelledby': 'milestone-form-dialog-title',
        },
      };
    },
  },
  watch: {
    initialMilestone: {
      immediate: true,
      handler(nextMilestone) {
        if (this.visible && nextMilestone) {
          this.resetFormFromMilestone(nextMilestone);
        }
      },
    },
  },
  methods: {
    handleShow() {
      this.resetFormFromMilestone(this.initialMilestone);
    },
    handleHide() {
      this.formErrors = {};
    },
    resetFormFromMilestone(milestone) {
      this.form = milestone
        ? mapMilestoneApiToDialog(milestone)
        : emptyMilestoneDialogForm();
      this.formErrors = {};
    },
    close() {
      this.visibleProxy = false;
    },
    submit() {
      const errors = validateMilestoneDialogForm(this.form, this.$t.bind(this));
      this.formErrors = errors;
      if (Object.keys(errors).length > 0) return;
      this.$emit('submit', mapMilestoneDialogToApiPayload(this.form));
    },
  },
};
</script>

<style scoped>
.milestone-form-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.milestone-form-dialog__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--color-dark);
}

.milestone-form-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--color-disabled);
  cursor: pointer;
}

.milestone-form-dialog__close:hover {
  color: var(--color-dark);
}

.milestone-form-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.milestone-form-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.milestone-form-dialog__label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-dark);
}

.milestone-form-dialog__required {
  color: var(--color-error);
}

.milestone-form-dialog__error {
  color: var(--color-error);
  font-size: 0.75rem;
}

.milestone-form-dialog__info {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.625rem;
  background: #f9f9f9;
}

.milestone-form-dialog__info-icon {
  color: #ababab;
  font-size: 1rem;
  margin-top: 0.125rem;
}

.milestone-form-dialog__info-text {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #ababab;
}

.milestone-form-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
}

.milestone-form-dialog__btn-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-dark);
  cursor: pointer;
}

.milestone-form-dialog__btn-cancel:hover {
  background: var(--color-light-grey);
}

.milestone-form-dialog__btn-submit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 0.625rem;
  background: var(--color-inactive);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-white);
  cursor: not-allowed;
}

.milestone-form-dialog__btn-submit--active:not(:disabled) {
  background: var(--accent-gradient);
  cursor: pointer;
}
</style>

<style>
.milestone-form-dialog.p-dialog {
  display: flex;
  flex-direction: column;
  width: min(520px, calc(100vw - 2rem));
  max-height: min(85vh, 44rem);
  margin: 1rem;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.milestone-form-dialog.p-dialog .p-dialog-header {
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.milestone-form-dialog.p-dialog .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
}

.milestone-form-dialog.p-dialog .p-dialog-footer {
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.milestone-form-dialog .milestone-form-dialog__input.p-inputtext {
  min-height: 2.5rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
}

.milestone-form-dialog .milestone-form-dialog__input.p-inputtext:enabled:focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 3px rgba(228, 130, 26, 0.15);
}

.milestone-form-dialog .milestone-form-dialog__multiselect.p-multiselect {
  min-height: 2.5rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
}

.milestone-form-dialog .p-dialog-mask {
  background: rgba(0, 0, 0, 0.4);
}
</style>
